/**
 * Reference modal listing the image sizes that cleanly fit each ad placement we serve, tabbed by
 * platform. Ratios mirror the exact rules in image-gallery.tsx's getPlatformFits (so the guidance
 * can't drift from the fit badges); pixel dimensions are the platforms' recommended/minimum sizes.
 * Opened from the asset toolbar's Help button on both the campaign-creation picker and /assets.
 */
import { useState } from 'react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TabCardPanel, tabButtonClass } from '@/components/glitch-ui/tab-card-panel';

import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';

type SizeItem = {
  name: string;
  /** Aspect ratio label, or '' for fixed-pixel placements (Google Display). */
  ratio: string;
  pixels: string;
  /** Nominal width/height for the aspect-shape graphic (any units; only the ratio matters). */
  w: number;
  h: number;
};
type PlacementGroup = { placement: string; items: SizeItem[]; footnote?: string };

const GUIDE: Record<'Meta' | 'Google', PlacementGroup[]> = {
  Meta: [
    {
      placement: 'Feed',
      items: [
        { name: 'Square', ratio: '1:1', pixels: '1080×1080 px', w: 1, h: 1 },
        { name: 'Portrait', ratio: '4:5', pixels: '1080×1350 px', w: 4, h: 5 },
        { name: 'Landscape', ratio: '1.91:1', pixels: '1200×628 px', w: 1.91, h: 1 },
      ],
    },
    {
      placement: 'Stories & Reels',
      items: [{ name: 'Vertical', ratio: '9:16', pixels: '1080×1920 px', w: 9, h: 16 }],
    },
  ],
  Google: [
    {
      placement: 'YouTube',
      items: [{ name: 'Landscape', ratio: '16:9', pixels: '1280×720 px (min)', w: 16, h: 9 }],
    },
    {
      placement: 'Display banners',
      items: [
        { name: 'Medium rectangle', ratio: '', pixels: '300×250 px', w: 300, h: 250 },
        { name: 'Large rectangle', ratio: '', pixels: '336×280 px', w: 336, h: 280 },
        { name: 'Leaderboard', ratio: '', pixels: '728×90 px', w: 728, h: 90 },
        { name: 'Half-page', ratio: '', pixels: '300×600 px', w: 300, h: 600 },
        { name: 'Wide skyscraper', ratio: '', pixels: '160×600 px', w: 160, h: 600 },
        { name: 'Billboard', ratio: '', pixels: '970×250 px', w: 970, h: 250 },
      ],
      footnote: 'Also accepted: 320×50, 320×100, 468×60, 250×250, 200×200, 970×90.',
    },
    {
      placement: 'Logo',
      items: [
        { name: 'Square', ratio: '1:1', pixels: '512×512 px (128 min)', w: 1, h: 1 },
        { name: 'Landscape', ratio: '4:1', pixels: '1024×256 px (512 min)', w: 4, h: 1 },
      ],
    },
  ],
};

const PLATFORM_LOGO: Record<'Meta' | 'Google', string> = { Meta: metaLogo, Google: googleAdsLogo };

/** A small cobalt box shaped to the given aspect ratio, centred in a fixed 44px tile. */
function AspectShape({ w, h }: { w: number; h: number }) {
  const MAX = 40;
  const width = w >= h ? MAX : Math.round(MAX * (w / h));
  const height = h >= w ? MAX : Math.round(MAX * (h / w));
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-gray-50">
      <div className="rounded-[2px] border border-[#290BD9]/40 bg-[#290BD9]/15" style={{ width, height }} />
    </div>
  );
}

interface AssetSizeGuideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssetSizeGuideDialog({ open, onOpenChange }: AssetSizeGuideDialogProps) {
  const [platform, setPlatform] = useState<'Meta' | 'Google'>('Meta');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Image Size Guide</DialogTitle>
          <DialogDescription>
            Images that match these sizes fill the placement with no cropping. Other images still upload, but
            may be cropped or letterboxed to fit.
          </DialogDescription>
        </DialogHeader>

        <TabCardPanel
          stickyTabs={false}
          tabs={(['Meta', 'Google'] as const).map((p) => ({
            id: p,
            label: p,
            render: () => (
              <button
                type="button"
                onClick={() => setPlatform(p)}
                className={`${tabButtonClass(platform === p)} flex items-center gap-2 cursor-pointer`}
              >
                {/* object-contain: Meta's mark is wider than tall, so a bare h-4 w-4 would squish it. */}
                <img src={PLATFORM_LOGO[p]} alt="" className="h-4 w-4 object-contain" />
                {p}
              </button>
            ),
          }))}
        >
          <div className="space-y-5">
            {GUIDE[platform].map((group) => (
              <div key={group.placement} className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.placement}</h4>
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <div key={`${item.name}-${item.pixels}`} className="flex items-center gap-3">
                      <AspectShape w={item.w} h={item.h} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">
                          {item.ratio ? `${item.ratio} · ${item.pixels}` : item.pixels}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {group.footnote && <p className="text-xs text-gray-400">{group.footnote}</p>}
              </div>
            ))}
          </div>
        </TabCardPanel>
      </DialogContent>
    </Dialog>
  );
}
